/**
 * Restore scroll position on back/forward button
 *
 * More about issue: https://github.com/vercel/next.js/issues/3303
 * Solution: https://gist.github.com/claus/992a5596d6532ac91b24abe24e10ae81
 * Edited:
 *  • Restore scroll position only when back/forward button was pressed
 *  • Keep scroll positions in local variable, not session storage, so refresh would reset scroll position
 */

import Router from 'next/router';
import { useEffect } from 'react';

const scrollPositions = {};
let shouldScrollRestore = false;
let lastUrl: string;
let restoreTimeout: number = null;

// Add restore timeout possibility for one route change event
export function setKeepScrollRestoreTimeoutOnce(timeout) {
  restoreTimeout = timeout;
}

function saveScrollPos(url) {
  scrollPositions[url] = {
    x: window.scrollX,
    y: window.scrollY,
  };
}

function restoreScrollPos(url) {
  const scrollPos = scrollPositions?.[url];
  const reset = () => {
    // Add one px to y scroll position...
    window.scrollTo(scrollPos?.x, scrollPos?.y + 1);

    // ...and scroll backwards to correct y scroll position in next event loop
    // in order to keep slidable header open after scrolling
    // (because header is hidden when scrolling down and shown when scrolling up)
    setTimeout(() => window.scrollTo(scrollPos?.x, scrollPos?.y), 0);

    // Reset timeout
    restoreTimeout = null;
  };

  if (scrollPos) {
    if (restoreTimeout !== null) {
      setTimeout(() => reset(), restoreTimeout);
    } else {
      reset();
    }
  }
}

function resetScrollPos() {
  window.scrollTo(0, 0);
}

export default function useScrollRestoration(router) {
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';

      // Save position before unload
      const onBeforeUnload = (event) => {
        saveScrollPos(router.asPath);
        delete event.returnValue;
      };

      // Save scroll position on route change start
      const onRouteChangeStart = () => {
        saveScrollPos(router.asPath);
      };

      // When route change complete, restore scroll position
      const onRouteChangeComplete = (url) => {
        // Check that doesn't run more than once per last url
        if (lastUrl === url) {
          return;
        }
        lastUrl = url;

        // If back/forward button was clicked, restore scroll position
        if (shouldScrollRestore) {
          restoreScrollPos(url);
        } else {
          // Otherwise reset scroll position
          resetScrollPos();
        }

        // Reset flag for next route to check if back/forward button was clicked
        shouldScrollRestore = false;
      };

      const beforePopState = () => {
        shouldScrollRestore = true;
      };

      window.addEventListener('beforeunload', onBeforeUnload);
      Router.events.on('routeChangeStart', onRouteChangeStart);
      Router.events.on('routeChangeComplete', onRouteChangeComplete);
      window.addEventListener('beforepopstate', beforePopState);

      return () => {
        window.removeEventListener('beforeunload', onBeforeUnload);
        Router.events.off('routeChangeStart', onRouteChangeStart);
        Router.events.off('routeChangeComplete', onRouteChangeComplete);
        window.removeEventListener('beforepopstate', beforePopState);
      };
    }
  }, [router]);
}
